import React, { useState } from 'react';
import { Button, CircularProgress, Tooltip } from '@mui/material';
import { JobTitleMappingCommand } from '../../../../../services/ticketEasyService/commands/jobTitleMapping/JobTitleMappingCommand';
import { useJobTitleMappingPostMutation } from '../../../../../services/ticketEasyService/ticketEasy-api';

interface JobTitleMapSaveButtonProps {
    assignments: { [key: string]: { relatedTitle: string; id: string } };
    onSaveSuccess?: () => void;
    onSaveError?: (error: any) => void;
}

/**
 * Button component for saving job title mappings with retry functionality
 */
function JobTitleMapSaveButton({ assignments, onSaveSuccess, onSaveError }: JobTitleMapSaveButtonProps) {
    const [postJobTitleMapping, { isLoading }] = useJobTitleMappingPostMutation();
    const [saveAttempted, setSaveAttempted] = useState(false);
    const [retryCount, setRetryCount] = useState(0);
    const MAX_RETRIES = 2;
    
    // Prepare the mapping array for save
    const prepareMappingArray = () => {
        return Object.entries(assignments).map(([jobTitle, { relatedTitle: relatedTitle, id }]) => ({
            jobTitle,
            relatedTitle: relatedTitle,
            jobType: 'HST',
            id: id ? id : '0',
        }));
    };

    // Attempt to save the mapping
    const attemptSave = async (retry = false) => {
        if (retry) {
            setRetryCount(prev => prev + 1);
        } else {
            setSaveAttempted(true);
        }
        
        const mapArray = prepareMappingArray();
        const command = new JobTitleMappingCommand(mapArray);
        
        try {
            // Execute the mutation
            await postJobTitleMapping(command).unwrap();
            
            // Ensure callbacks run after React's render cycle
            setTimeout(() => {
                if (onSaveSuccess) {
                    onSaveSuccess();
                }
                // Reset retry count on success
                setRetryCount(0);
            }, 0);
        } catch (error) {
            // Attempt retry if we haven't exceeded the maximum
            if (retryCount < MAX_RETRIES) {
                setTimeout(() => attemptSave(true), 1000); // Wait 1 second before retry
            } else {
                if (onSaveError) {
                    onSaveError(error);
                }
                // Reset retry count after reporting the error
                setRetryCount(0);
            }
        }
    };

    const handleSave = () => {
        attemptSave();
    };

    // Check if there are any valid mappings to save
    const hasValidMappings = Object.values(assignments).some(
        assignment => assignment.relatedTitle && assignment.relatedTitle.trim() !== ''
    );

    return (
        <Tooltip title={!hasValidMappings ? "You need to select at least one HST role to save" : ""}>
            <span>
                <Button 
                    variant="contained" 
                    color="primary" 
                    onClick={handleSave}
                    disabled={isLoading || !hasValidMappings}
                    startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : null}
                >
                    {isLoading ? (retryCount > 0 ? `Retrying (${retryCount})...` : 'Saving...') : 'Save'}
                </Button>
            </span>
        </Tooltip>
    );
}

export { JobTitleMapSaveButton };
